import { useEffect, useMemo, useRef, useState } from "react";
import { useCatalog } from "../lib/catalog-context";
import { filterPlugins, extractCategories } from "../lib/search";
import FilterBar from "../components/FilterBar";
import PluginCard from "../components/PluginCard";
import PluginListView from "../components/PluginListView";
import CatalogToolbar from "../components/CatalogToolbar";
import type { SortOption, ViewMode } from "../components/CatalogToolbar";
import SearchBar from "../components/SearchBar";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import GettingStarted from "../components/GettingStarted";
import type { ExtensionType, MarketplaceEntry, SupportedClient } from "../types/index";
import styles from "./CatalogPage.module.css";

const CLIENT_FILTER_OPTIONS: { value: SupportedClient; label: string }[] = [
  { value: "claude-chat",   label: "Claude Chat" },
  { value: "claude-cowork", label: "Claude Cowork" },
  { value: "claude-code",   label: "Claude Code" },
];

type TypeFilter = "all" | ExtensionType;

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all",    label: "All" },
  { value: "plugin", label: "Plugins" },
  { value: "skill",  label: "Skills" },
];

const SORT_ORDER_MAP: Record<SortOption, "asc" | "desc"> = {
  name: "asc",
  updated: "desc",
  created: "desc",
  official: "desc", // ignored by the official sort (Official-first is a fixed order)
};

export const PAGE_SIZE = 30;

function getEmptyStateMessage(
  query: string,
  clients: SupportedClient[],
  categories: string[],
  typeFilter: TypeFilter = "all",
  officialOnly = false,
): string {
  const noun =
    typeFilter === "plugin" ? "plug-ins"
    : typeFilter === "skill" ? "skills"
    : "plug-ins or skills";
  const parts: string[] = [];
  if (query) parts.push(`matching "${query}"`);
  if (officialOnly) parts.push("marked Official CAI");
  if (clients.length > 0) {
    const labels = clients.map((c) => CLIENT_FILTER_OPTIONS.find((o) => o.value === c)?.label ?? c);
    parts.push(`for ${labels.join(" or ")}`);
  }
  if (categories.length === 1) parts.push(`in ${categories[0]}`);
  else if (categories.length > 1) parts.push(`in ${categories.join(" or ")}`);
  return parts.length > 0 ? `No ${noun} ${parts.join(" ")}` : `No ${noun} available`;
}

interface CatalogPageProps {
  onSelectPlugin: (plugin: MarketplaceEntry) => void;
  initialClients?: string[];
  initialCategories?: string[];
  initialQuery?: string;
  initialType?: ExtensionType;
  initialOfficial?: boolean;
  onHelp?: () => void;
  onFiltersChange?: (query: string, clients: SupportedClient[], categories: string[], type?: ExtensionType, official?: boolean) => void;
}

export default function CatalogPage({ onSelectPlugin, initialClients, initialCategories, initialQuery, initialType, initialOfficial, onHelp, onFiltersChange }: CatalogPageProps) {
  // Catalog data from the shared provider (one fetch, one source of truth).
  const { plugins, isLoading, error, refresh } = useCatalog();

  // Sorted view of the catalog — updated whenever the provider's plugins change.
  const allPlugins = useMemo(
    () =>
      [...plugins].sort((a, b) =>
        a.pluginName.toLowerCase().localeCompare(b.pluginName.toLowerCase()),
      ),
    [plugins],
  );

  // Categories derived from the full sorted list.
  const availableCategories = useMemo(() => extractCategories(allPlugins), [allPlugins]);

  const [clientFilters, setClientFilters] = useState<SupportedClient[]>((initialClients as SupportedClient[]) ?? []);
  const [categoryFilters, setCategoryFilters] = useState<string[]>(initialCategories ?? []);
  const [searchQuery, setSearchQuery] = useState(initialQuery ?? "");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(initialType ?? "all");
  const [sortBy, setSortBy] = useState<SortOption>("name");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [officialOnly, setOfficialOnly] = useState(initialOfficial ?? false);
  const [searchBarClearToken, setSearchBarClearToken] = useState(0);

  // Filtered results derived from the catalog + active filters. Deriving (rather
  // than holding in state and syncing via effects) keeps results correct by
  // construction — including after a refresh() that updates plugins in place.
  const results = useMemo(
    () =>
      filterPlugins(allPlugins, {
        query: searchQuery,
        supportedClients: clientFilters.length > 0 ? clientFilters : undefined,
        categories: categoryFilters.length > 0 ? categoryFilters : undefined,
        extensionType: typeFilter === "all" ? undefined : typeFilter,
        officialOnly,
        sortBy,
        sortOrder: SORT_ORDER_MAP[sortBy],
      }),
    [allPlugins, searchQuery, clientFilters, categoryFilters, typeFilter, sortBy, officialOnly],
  );

  // Sync active filters to the URL on user changes. Skip the initial render so we
  // don't echo the URL-derived seed values straight back. Sort is intentionally
  // not synced (matches prior behaviour).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    onFiltersChange?.(
      searchQuery,
      clientFilters,
      categoryFilters,
      typeFilter === "all" ? undefined : typeFilter,
      officialOnly,
    );
    // onFiltersChange identity is not a filter input; fire only on filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, clientFilters, categoryFilters, typeFilter, officialOnly]);

  const handleSearch = (query: string) => setSearchQuery(query);

  // Reset the visible window to the first page whenever the filtered results change.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [results]);

  const sentinelVisible = visibleCount < results.length;
  // Keyed on `sentinelVisible` (a boolean), not `visibleCount`: the observer is
  // recreated only when crossing the "more to show / nothing more" boundary, so
  // each PAGE_SIZE grow reuses the same observer instead of tearing it down and
  // rebuilding it every batch. Keying on `visibleCount` regressed once (commit
  // d762e41): because visibleCount changes on every grow, the effect re-ran each
  // batch — disconnecting and rebuilding a fresh IntersectionObserver every time
  // instead of leaving one in place. The boolean key holds it stable across grows.
  //
  // `viewMode` is in the deps as defensive hardening, not a fix for a live bug.
  // Today the grid and list branches both render the sentinel at the same index
  // of a keyless fragment, so React reuses the same DOM node across a view switch
  // and the observer stays bound to a connected element (verified: the marked node
  // survives the switch, still connected). But that reuse is an implicit invariant
  // of the JSX shape — a future refactor that changes it would remount the sentinel
  // and strand the observer. Re-running on `viewMode` re-observes the current node
  // regardless; the cost is one disconnect/observe per (rare) manual view toggle.
  useEffect(() => {
    if (!sentinelVisible) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, results.length));
        }
      },
      { rootMargin: "400px" }, // prefetch the next page ~400px before the sentinel scrolls into view
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelVisible, results.length, viewMode]);

  const toggleFilter = <T,>(prev: T[], value: T, eq: (a: T, b: T) => boolean): T[] =>
    prev.some((v) => eq(v, value)) ? prev.filter((v) => !eq(v, value)) : [...prev, value];

  const handleClientToggle = (client: SupportedClient) =>
    setClientFilters((prev) => toggleFilter(prev, client, (a, b) => a === b));

  const handleCategoryToggle = (category: string) =>
    setCategoryFilters((prev) =>
      toggleFilter(prev, category, (a, b) => a.toLowerCase() === b.toLowerCase()),
    );

  const handleTypeChange = (type: TypeFilter) => setTypeFilter(type);

  const handleSortChange = (sort: SortOption) => setSortBy(sort);

  const handleOfficialToggle = () => setOfficialOnly((prev) => !prev);

  if (error) {
    return (
      <div className={styles.errorState}>
        <h1>Error Loading Marketplace</h1>
        <p className={styles.errorMessage}>{error}</p>
        <button onClick={() => refresh()} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.header}>
        <h1>AI Marketplace</h1>
        <p className={styles.subtitle}>
          Browse and discover available AI plug-ins and skills for Cox Auto approved LLM clients.
        </p>
      </div>

      <GettingStarted onHelp={onHelp} />
      <SearchBar onSearch={handleSearch} isLoading={isLoading} externalQuery={searchQuery} clearToken={searchBarClearToken}  placeholder="Search plug-ins and skills..." />
      <p className={styles.totals}>
        Total available: {allPlugins.filter((p) => p.extensionType === "skill").length} skills · {allPlugins.filter((p) => p.extensionType === "plugin").length} plug-ins
      </p>

      <CatalogToolbar
        sortBy={sortBy}
        onSortChange={handleSortChange}
        viewMode={viewMode}
        onViewChange={setViewMode}
        officialOnly={officialOnly}
        onOfficialToggle={handleOfficialToggle}
      >
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>
            Type:
          </span>
          <div role="group" aria-label="Filter by type" className={styles.toggleGroup}>
            {TYPE_FILTER_OPTIONS.map((opt) => {
              const active = typeFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleTypeChange(opt.value)}
                  aria-pressed={active}
                  className={`${styles.toggleButton}${active ? ` ${styles.active}` : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>
            Works with:
          </span>
          <div role="group" aria-label="Filter by client" className={styles.toggleGroup}>
            {CLIENT_FILTER_OPTIONS.map((opt) => {
              const active = clientFilters.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => handleClientToggle(opt.value)}
                  aria-pressed={active}
                  className={`${styles.toggleButton}${active ? ` ${styles.active}` : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <FilterBar
          categories={availableCategories}
          activeCategories={categoryFilters}
          onCategoryToggle={handleCategoryToggle}
          labelMinWidth="6rem"
        />
      </CatalogToolbar>

      {isLoading && !allPlugins.length ? (
        <Loading message="Loading catalog..." />
      ) : results.length === 0 ? (
        <EmptyState message={getEmptyStateMessage(searchQuery, clientFilters, categoryFilters, typeFilter, officialOnly)}>
          {(searchQuery || clientFilters.length > 0 || categoryFilters.length > 0 || typeFilter !== "all" || officialOnly) && (
            <button
              onClick={() => {
                setClientFilters([]);
                setCategoryFilters([]);
                setSearchQuery("");
                setTypeFilter("all");
                setOfficialOnly(false);
                setSearchBarClearToken((t) => t + 1);
              }}
              className={styles.clearButton}
            >
              Clear filters
            </button>
          )}
        </EmptyState>
      ) : viewMode === "list" ? (
        <>
          <PluginListView plugins={results.slice(0, visibleCount)} onSelectPlugin={onSelectPlugin} />
          {sentinelVisible && <div ref={sentinelRef} aria-hidden="true" />}
        </>
      ) : (
        <>
          <div className={styles.grid}>
            {results.slice(0, visibleCount).map((plugin) => (
              <PluginCard
                key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
                plugin={plugin}
                onClick={() => onSelectPlugin(plugin)}
                onClientClick={handleClientToggle}
                onCategoryClick={handleCategoryToggle}
              />
            ))}
          </div>
          {sentinelVisible && <div ref={sentinelRef} aria-hidden="true" />}
        </>
      )}
    </div>
  );
}
